import * as React from "react";

import { Avatar, Box, Code, Container, Stack, Text, Title } from "@mantine/core";
import { IconBan, IconBug, IconChecklist, IconLockCheck, IconTrademark } from "@tabler/icons-react";
import { Helmet } from "react-helmet";

import { useGoatCounter } from "../../hooks/useGoatCounter";
import fhats from "../../images/fhats.jpg";

export const AboutPage = (): JSX.Element => {
  const eventCounter = useGoatCounter();
  return (
    <Container size="md" py="md" px={90} style={{ height: "100%" }}>
      <Helmet>
        <title>About</title>
      </Helmet>
      <Stack spacing="xl">
        <AboutSection left={<IconChecklist size={56} className="about-left" />}>
          <Title order={3}>About fs-checklist-studio</Title>
          <Stack spacing="sm">
            <Text>
              fs-checklist-studio is a browser-based application for authoring realistic-looking
              checklists for use with Flight Simulators. You can create a checklist from scratch or
              use a combination of dozens of included templates based on common flight simulator
              addon software, then customize to your liking. When you're done, you can download the
              resulting checklists as PDF.
            </Text>
            <Text>
              <strong>Note:</strong> Checklists are stored locally in your browser. You can close
              the window and come back later and your checklists will still be here, but you cannot
              access your checklists from another computer (yet).
            </Text>
          </Stack>
        </AboutSection>
        <AboutSection left={<IconBug size={56} className="about-left" />}>
          <Title order={3}>Found a bug? Missing an addon?</Title>
          <Stack spacing="sm">
            <Text>
              If you found a bug, can't find your favorite addon, or have an idea for a new feature,
              I'd love to know! You can drop me a line by email to <Code>hi</Code> at (this web
              address).
            </Text>
            <Text>
              I work on fs-checklist-studio in my spare time. You are bound to come across something
              that doesn't work quite right sooner or later. I'll do my level best to keep things in
              working order, but please have some patience with me if I fall behind :)
            </Text>
          </Stack>
        </AboutSection>
        <AboutSection left={<Avatar src={fhats} size="lg" radius="xl" className="about-left" />}>
          <Title order={3}>About me</Title>
          <Stack spacing="sm">
            <Text>
              Hi! I'm{" "}
              <a
                href="https://fredhatfull.com"
                onClick={() => {
                  eventCounter("fhatsweb-link");
                }}
                target="_blank"
              >
                Fred
              </a>{" "}
              and I'm the author of fs-checklist-studio. I love flight simulators but frequently
              forget to set up, configure, and start many of the addons I fly with. I created
              fs-checklist-studio because I wanted an easy way to create and amend my own checklists
              without futzing around with a word processor.
            </Text>
            <Text>
              I write computer software for a living and in my spare time I make up-tempo rock music
              in a band called{" "}
              <a
                href="https://quickfiction.band"
                onClick={() => {
                  eventCounter("qf-link");
                }}
              >
                Quick Fiction
              </a>
              . You can find our music on{" "}
              <a
                href="https://quickfiction.bandcamp.com/album/fingers-crossed"
                data-goatcounter-click="qf-spotify-click"
              >
                Bandcamp
              </a>
              ,{" "}
              <a
                href="https://open.spotify.com/album/5rEbhf3BW3xW8oldqrLX2B?si=s5ZO-eaeTZSzPJ4srqIQVA"
                data-goatcounter-click="qf-spotify-click"
              >
                Spotify
              </a>
              ,{" "}
              <a
                href="https://music.apple.com/us/artist/quick-fiction/1580793555"
                data-goatcounter-click="qf-spotify-click"
              >
                Apple Music
              </a>
              , and most other music streaming providers.
            </Text>
          </Stack>
        </AboutSection>
        <AboutSection left={<IconLockCheck size={56} className="about-left" />}>
          <Title order={3}>About your data</Title>
          <Stack spacing="sm">
            <Text>
              fs-checklist-studio runs entirely in your browser, and doesn't collect any personal
              information or data beyond what you enter into your checklists. Checklists you create
              are stored locally in your browser's{" "}
              <a href="https://developer.mozilla.org/en-US/docs/Web/API/Web_Storage_API">
                local storage
              </a>
              and never leave your computer unless you choose to share them. Your checklists can't
              yet be transferred between computers -- sorry! Please let me know if this
              functionality would be useful for you.
            </Text>
            <Text>
              fs-checklist-studio uses three third party tools to report errors and aggregated usage
              information: <a href="https://bugsnag.com">BugSnag</a>,{" "}
              <a href="https://simpleanalytics.com">Simple Analytics</a> and{" "}
              <a href="https://goatcounter.com">GoatCounter</a>. None of these tools collect any
              personal information about you. BugSnag lets me know when errors happen so that I can
              find and fix them. Simple Analytics and GoatCounter let me know how many times various
              parts of the site are used, mostly for my own curiosity.
            </Text>
            <Text>
              If you use an ad blocker, it will probably block all of these tools. I'd appreciate it
              if you allowed these tools to collect their data so that I can keep things running
              smoothly, but I understand if you choose to leave them blocked.
            </Text>
          </Stack>
        </AboutSection>
        <AboutSection left={<IconTrademark size={56} className="about-left" />}>
          <Title order={3}>About trademarks</Title>
          <Stack spacing="sm">
            <Text>
              Just in case it wasn't obvious, all trademarks for flight simulators, addon software,
              and other utilities referenced by this program are property of their respective
              owners.
            </Text>
          </Stack>
        </AboutSection>
        <AboutSection left={<IconBan size={56} className="about-left" />}>
          <Title order={3}>Do not use these checklists to operate real aircraft</Title>
          <Stack spacing="sm">
            <Text>
              The template checklists included in this program are intended for use exclusively with{" "}
              <strong>
                flight simulators <i>only</i>
              </strong>
              . These checklists have not been reviewed by any aircraft manufacturer or regulatory
              authority and should not be used for real-world operation of any aircraft.
            </Text>
          </Stack>
        </AboutSection>
      </Stack>
    </Container>
  );
};

const AboutSection = ({
  children,
  left,
}: {
  children: React.ReactNode;
  left: React.ReactNode;
}): JSX.Element => {
  return (
    <Stack spacing={0}>
      <Box style={{ display: "block" }}>
        {left}
        <Stack spacing={0}>{children}</Stack>
      </Box>
    </Stack>
  );
};
